import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import TierBadge from './TierBadge';
import { API_URL, formatCurrency } from '../demoMode';
import { PrimaryButton, ScreenShell } from './ScreenShared';

const ZERO_SUMMARY = {
  balance: '0',
  currency: 'USD',
  recent_entries: [],
};

function toNumber(value) {
  const numeric = Number.parseFloat(value ?? '0');
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatMoney(value, currency = 'USD') {
  return formatCurrency(toNumber(value), currency);
}

const LEGAL_PRINT_HEADER = /^Instant Connections — (?:Privacy Policy|Terms and Conditions of Service)$/;
const LEGAL_PRINT_FOOTER = /^Instant Connections Corp\. • legal@ic\.social • Page \d+ of \d+$/;

function parseLegalDocument(source) {
  const lines = source
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => !LEGAL_PRINT_HEADER.test(line) && !LEGAL_PRINT_FOOTER.test(line));
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    if (!lines[index]) {
      index += 1;
      continue;
    }

    if (lines[index].startsWith('• ')) {
      const items = [];
      while (index < lines.length && lines[index].startsWith('• ')) {
        items.push(lines[index].slice(2));
        index += 1;
        while (index < lines.length && lines[index] && !lines[index].startsWith('• ')) {
          items[items.length - 1] += ` ${lines[index]}`;
          index += 1;
        }
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    if (/^1\.\s+/.test(lines[index]) && /^2\.\s+/.test(lines[index + 1] || '')) {
      const items = [];
      while (index < lines.length && new RegExp(`^${items.length + 1}\\.\\s+`).test(lines[index])) {
        items.push(lines[index].replace(/^\d+\.\s+/, ''));
        index += 1;
        while (index < lines.length && lines[index] && !/^\d+\.\s+/.test(lines[index])) {
          items[items.length - 1] += ` ${lines[index]}`;
          index += 1;
        }
      }
      blocks.push({ type: 'ordered-list', items });
      continue;
    }

    const text = lines[index];
    const heading = text.match(/^(\d{1,2}[a-z]?)\.\s+(.+)$/);
    if (heading) {
      blocks.push({ type: /[a-z]$/.test(heading[1]) ? 'subheading' : 'heading', text });
      index += 1;
      continue;
    }

    const paragraph = [text];
    index += 1;
    while (index < lines.length && lines[index] && !lines[index].startsWith('• ') && !/^\d{1,2}[a-z]?\.\s+/.test(lines[index])) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
  }

  return blocks;
}

function LegalDocument({ path }) {
  const [document, setDocument] = useState({ status: 'loading', blocks: [] });

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL}${path}`)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load this document.');
        return response.text();
      })
      .then((source) => {
        if (active) setDocument({ status: 'ready', blocks: parseLegalDocument(source) });
      })
      .catch(() => {
        if (active) setDocument({ status: 'error', blocks: [] });
      });

    return () => {
      active = false;
    };
  }, [path]);

  if (document.status === 'loading') return <p className="ic-legal-status">Loading document...</p>;
  if (document.status === 'error') return <p className="ic-legal-status">This document could not be loaded. Please try again later.</p>;

  return document.blocks.map((block, index) => {
    if (block.type === 'heading') return <h3 key={index}>{block.text}</h3>;
    if (block.type === 'subheading') return <h4 key={index}>{block.text}</h4>;
    if (block.type === 'list' || block.type === 'ordered-list') {
      const List = block.type === 'list' ? 'ul' : 'ol';
      return <List key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</List>;
    }
    return <p key={index}>{block.text}</p>;
  });
}

function useWalletSummary(refreshKey = 0) {
  const [summary, setSummary] = useState(ZERO_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const fetchSummary = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (active) {
          setSummary(ZERO_SUMMARY);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError('');

        const response = await fetch(`${API_URL}/v1/wallet`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.detail || 'Unable to load wallet data.');
        }

        const payload = await response.json();
        if (active) {
          setSummary({ ...ZERO_SUMMARY, ...payload });
        }
      } catch (err) {
        if (active) {
          setSummary(ZERO_SUMMARY);
          setError(err?.message || 'Unable to load wallet data.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  return { summary, loading, error };
}

function useWalletActions(onRefresh) {
  const [topUpAmount, setTopUpAmount] = useState('25');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const submitTopUp = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setActionError('Please sign in again to add funds.');
      return;
    }

    const amount = Number.parseFloat(topUpAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setActionError('Enter a valid amount.');
      return;
    }

    try {
      setSubmitting(true);
      setActionError('');

      const response = await fetch(`${API_URL}/v1/wallet/top-up`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ amount }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Unable to add funds.');
      }

      setTopUpAmount('25');
      onRefresh((value) => value + 1);
    } catch (err) {
      setActionError(err?.message || 'Unable to add funds.');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    topUpAmount,
    setTopUpAmount,
    submitTopUp,
    submitting,
    actionError,
  };
}

export function WalletScreen() {
  const [refreshKey, setRefreshKey] = useState(0);
  const { summary, loading, error } = useWalletSummary(refreshKey);
  const { topUpAmount, setTopUpAmount, submitTopUp, submitting, actionError } = useWalletActions(setRefreshKey);

  const spent = useMemo(() => {
    return summary.recent_entries
      .filter((entry) => entry?.direction === 'debit')
      .reduce((total, entry) => total + Math.abs(toNumber(entry?.amount)), 0);
  }, [summary.recent_entries]);

  return (
    <ScreenShell>
      <div className="ic-balance-card">
        <p className="ic-balance-label">Balance</p>
        <p className="ic-balance-value">{loading ? 'Loading...' : formatMoney(summary.balance, summary.currency)}</p>
        <p className="ic-balance-sub">Spent: {loading ? 'Loading...' : formatMoney(spent, summary.currency)}</p>
      </div>

      <div className="ic-topup-panel">
        <h3>Add funds</h3>
        <div className="ic-topup-input-row">
          <input
            type="number"
            min="0.01"
            max="250"
            step="0.01"
            value={topUpAmount}
            onChange={(event) => setTopUpAmount(event.target.value)}
            placeholder="Amount"
          />
          <button onClick={submitTopUp} disabled={submitting}>{submitting ? 'Adding...' : 'Top up'}</button>
        </div>
        <p>This uses the temporary in-app wallet until bank payments are approved.</p>
        {actionError ? <p className="figma-sub">{actionError}</p> : null}
        {error ? <p className="figma-sub">{error}</p> : null}
      </div>

      <div className="ic-history-list">
        {summary.recent_entries.slice(0, 6).map((entry) => (
          <div className="ic-history-row" key={entry.id}>
            <span>{entry.description || entry.type}</span>
            <strong>{entry.direction === 'debit' ? '-' : '+'}{formatMoney(Math.abs(toNumber(entry.amount)), summary.currency)}</strong>
          </div>
        ))}
        {!loading && summary.recent_entries.length === 0 ? (
          <div className="ic-history-row"><span>No wallet activity yet</span><strong>—</strong></div>
        ) : null}
      </div>

      <PrimaryButton to="/earnings">View earnings</PrimaryButton>
    </ScreenShell>
  );
}

export function EarningsScreen() {
  const { summary, loading, error } = useWalletSummary();

  const { todayCredits, weekCredits } = useMemo(() => {
    const now = new Date();
    const nowMs = now.getTime();
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekStart = nowMs - 7 * 24 * 60 * 60 * 1000;

    return summary.recent_entries
      .filter((entry) => entry?.direction === 'credit')
      .reduce(
        (acc, entry) => {
          const createdAtMs = Date.parse(entry?.created_at || '');
          const amount = Math.abs(toNumber(entry?.amount));

          if (Number.isFinite(createdAtMs) && createdAtMs >= dayStart) {
            acc.todayCredits += amount;
          }
          if (Number.isFinite(createdAtMs) && createdAtMs >= weekStart) {
            acc.weekCredits += amount;
          }

          return acc;
        },
        { todayCredits: 0, weekCredits: 0 }
      );
  }, [summary.recent_entries]);

  return (
    <ScreenShell>
      <div className="ic-balance-card">
        <p className="ic-balance-label">Available</p>
        <p className="ic-balance-value">{loading ? 'Loading...' : `+${formatMoney(weekCredits, summary.currency)}`}</p>
        <p className="ic-balance-sub">Today: {loading ? 'Loading...' : `+${formatMoney(todayCredits, summary.currency)}`}</p>
      </div>

      <div className="ic-history-list">
        {summary.recent_entries
          .filter((entry) => entry?.direction === 'credit')
          .slice(0, 8)
          .map((entry) => (
            <div className="ic-history-row" key={entry.id}>
              <span>{entry.description || entry.type}</span>
              <strong>+{formatMoney(Math.abs(toNumber(entry.amount)), summary.currency)}</strong>
            </div>
          ))}
      </div>
      {error ? <p className="figma-sub">{error}</p> : null}
      <PrimaryButton to="/wallet">Back to wallet</PrimaryButton>
    </ScreenShell>
  );
}

function IdentityVerificationCard({ isVerified, onVerified }) {
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!session) return undefined;

    const token = localStorage.getItem('token');
    if (!token) return undefined;

    let active = true;
    setPolling(true);

    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/v1/user/verify-id/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => null);
        if (!active) return;

        if (response.ok && payload?.is_verified) {
          clearInterval(interval);
          setPolling(false);
          setSession(null);
          onVerified();
        }
      } catch (_err) {
        // Keep polling; a transient network error shouldn't close the flow.
      }
    }, 4000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [session, onVerified]);

  const startVerification = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setStarting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/v1/user/verify-id/session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.detail || 'Identity verification is unavailable right now.');
      }

      setSession(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start verification.');
    } finally {
      setStarting(false);
    }
  };

  if (isVerified) {
    return (
      <div className="ic-profile-menu ic-verify-card">
        <div className="ic-menu-row"><span>Identity verification</span><span>Verified</span></div>
      </div>
    );
  }

  return (
    <div className="ic-profile-menu ic-verify-card">
      <div className="ic-menu-row">
        <span>Identity verification</span>
        <button type="button" className="figma-btn figma-btn-secondary" onClick={startVerification} disabled={starting}>
          {starting ? 'Starting...' : 'Verify'}
        </button>
      </div>
      {error ? <p className="figma-sub ic-verify-error">{error}</p> : null}

      {session ? (
        <div className="ic-verify-overlay" role="dialog" aria-label="Identity verification">
          <div className="ic-verify-sheet">
            <div className="ic-verify-sheet-head">
              <span>{polling ? 'Waiting for verification...' : 'Verify your identity'}</span>
              <button type="button" className="ic-verify-close" onClick={() => setSession(null)}>Close</button>
            </div>
            <iframe
              title="Yoti identity verification"
              src={session.iframe_url}
              allow="camera"
              className="ic-verify-iframe"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function ProfileScreen() {
  const [profile, setProfile] = useState(null);
  const [draft, setDraft] = useState({ age: '', gender: '', rate_per_hour: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    let active = true;

    const loadProfile = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/v1/user/me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json().catch(() => null);
        if (active && response.ok && payload?.username) {
          setProfile(payload);
          setDraft({
            age: payload.age ?? '',
            gender: payload.gender ?? '',
            rate_per_hour: payload.rate_per_hour ?? '',
          });
        }
      } catch (_err) {
        if (active) {
          setProfile(null);
        }
      }
    };

    loadProfile();
    return () => {
      active = false;
    };
  }, []);

  const handleSave = async (event) => {
    event.preventDefault();
    const token = localStorage.getItem('token');
    if (!token || !profile?.username) return;

    setSaving(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      const body = {
        age: draft.age === '' ? null : Number(draft.age),
        gender: draft.gender === '' ? null : draft.gender,
        rate_per_hour: draft.rate_per_hour === '' ? null : String(draft.rate_per_hour),
      };

      const response = await fetch(`${API_URL}/v1/user/${encodeURIComponent(profile.username)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.detail || `Could not save profile (HTTP ${response.status}).`);
      }

      setProfile((current) => ({ ...current, ...body }));
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const displayName = profile?.name || profile?.username || 'You';

  return (
    <ScreenShell>
      <div className="ic-profile-scroll-panel">
        <div className="ic-profile-hero">
          <div className="ic-profile-avatar-lg">{displayName.slice(0, 1).toUpperCase()}</div>
          <h2>{displayName}</h2>
          <div className="ic-profile-pills">
            <span>Tier <TierBadge tier={profile?.tier_id} /></span>
            <span>{profile?.username ? `@${profile.username}` : 'Member'}</span>
          </div>
        </div>

        {profile ? (
          <form className="ic-profile-edit-form" onSubmit={handleSave}>
            <h3 className="ic-profile-edit-title">Lounge details</h3>
            <p className="figma-sub">Age, gender, and rate are used to filter you in the Lounge.</p>

            <label className="ic-profile-field">
              <span>Age</span>
              <input
                type="number"
                min={18}
                max={120}
                value={draft.age}
                onChange={(event) => setDraft((current) => ({ ...current, age: event.target.value }))}
                placeholder="Not set"
              />
            </label>

            <label className="ic-profile-field">
              <span>Gender</span>
              <select
                value={draft.gender}
                onChange={(event) => setDraft((current) => ({ ...current, gender: event.target.value }))}
              >
                <option value="">Not set</option>
                <option value="woman">Woman</option>
                <option value="man">Man</option>
                <option value="non-binary">Non-binary</option>
              </select>
            </label>

            <label className="ic-profile-field">
              <span>Hourly rate (USD)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={draft.rate_per_hour}
                onChange={(event) => setDraft((current) => ({ ...current, rate_per_hour: event.target.value }))}
                placeholder="0"
              />
            </label>

            {saveError ? <p className="figma-sub">{saveError}</p> : null}
            {saveSuccess ? <p className="figma-sub">Saved.</p> : null}

            <button type="submit" className="figma-btn figma-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </form>
        ) : null}

      <IdentityVerificationCard
        isVerified={Boolean(profile?.is_verified)}
        onVerified={() => setProfile((current) => (current ? { ...current, is_verified: true } : current))}
      />

      <div className="ic-profile-menu">
        <Link to="/settings" className="ic-menu-row"><span>Settings</span><span>Open</span></Link>
        <Link to="/privacy" className="ic-menu-row"><span>Privacy</span><span>Open</span></Link>
        <Link to="/terms" className="ic-menu-row"><span>Terms</span><span>Open</span></Link>
      </div>
      </div>
    </ScreenShell>
  );
}

export function SettingsScreen() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState({
    invites: true,
    callAccepted: true,
    promos: false,
  });

  return (
    <ScreenShell
      titleBar={(
        <div className="ic-legal-topbar">
          <button type="button" className="ic-legal-back" onClick={() => navigate('/profile')} aria-label="Go back to profile" title="Back to profile">
            <ArrowLeft aria-hidden="true" size={18} />
          </button>
          <h1>Settings</h1>
        </div>
      )}
    >
      <div className="ic-settings-group">
        <p>Account</p>
        <Link to="/privacy" className="ic-menu-row"><span>Privacy policy</span><span>View</span></Link>
        <Link to="/terms" className="ic-menu-row"><span>Terms of service</span><span>View</span></Link>
      </div>
      <div className="ic-settings-group">
        <p>Notifications</p>
        <label className="ic-toggle-row">
          <span>New invite received</span>
          <input type="checkbox" checked={notifications.invites} onChange={() => setNotifications((s) => ({ ...s, invites: !s.invites }))} />
        </label>
        <label className="ic-toggle-row">
          <span>Call accepted</span>
          <input type="checkbox" checked={notifications.callAccepted} onChange={() => setNotifications((s) => ({ ...s, callAccepted: !s.callAccepted }))} />
        </label>
        <label className="ic-toggle-row">
          <span>Promotions</span>
          <input type="checkbox" checked={notifications.promos} onChange={() => setNotifications((s) => ({ ...s, promos: !s.promos }))} />
        </label>
      </div>
    </ScreenShell>
  );
}

export function PrivacyScreen() {
  const navigate = useNavigate();

  return (
    <ScreenShell
      titleBar={(
        <div className="ic-legal-topbar">
          <button type="button" className="ic-legal-back" onClick={() => navigate(-1)} aria-label="Go back" title="Go back">
            <ArrowLeft aria-hidden="true" size={18} />
          </button>
          <h1>Privacy Policy</h1>
        </div>
      )}
      showMainNav={false}
    >
      <div className="ic-legal-scroll">
        <LegalDocument path="/legal/privacy.md" />
      </div>
    </ScreenShell>
  );
}

export function TermsScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === 'true';

  return (
    <ScreenShell
      titleBar={(
        <div className="ic-legal-topbar">
          <button type="button" className="ic-legal-back" onClick={() => navigate(-1)} aria-label="Go back" title="Go back">
            <ArrowLeft aria-hidden="true" size={18} />
          </button>
          <h1>Terms &amp; Conditions</h1>
        </div>
      )}
      showMainNav={false}
    >
      <div className="ic-legal-scroll">
        <LegalDocument path="/legal/terms.md" />
      </div>
      {isOnboarding ? <PrimaryButton to="/auth">Agree & continue</PrimaryButton> : null}
    </ScreenShell>
  );
}
